import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { ChevronRight, Search, Truck, Gauge, Clock, User, Navigation, ChevronLeft, Activity, MapPin, Download } from 'lucide-react';
import { dashboardService } from '../services/dashboardService';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { settingsService } from '../services/settingsService';
import { toast } from 'sonner';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import * as XLSX from 'xlsx-js-style';

type DashboardPoint = {
  id: string;
  type: 'pickup' | 'delivery' | 'driver' | 'vehicle';
  shipmentId: number;
  shipmentNo: string;
  label: string;
  subtitle: string;
  latitude: number;
  longitude: number;
  status: string;
  stage: number;
  speed?: number;
  isRunning?: boolean;
  address?: string;
  dailyKm?: number | null;
  dailyFuel?: number | null;
  fuelLevel?: number | null;
  ignitionOn?: string | null;
  ignitionOff?: string | null;
  dailyIdleTime?: string | null;
  dailyWorkTime?: string | null;
  dailyDriveTime?: string | null;
  currentWeekHours?: string | null;
  twoWeekHours?: string | null;
  locationUpdatedAt?: string | null;
  driverName?: string | null;
  driverPhone?: string | null;
  seyirDriverName?: string | null;
  trailerPlate?: string | null;
  activeShipmentId?: number | null;
  activeShipmentNo?: string | null;
  activeShipmentSummaries?: string | null;
  activeShipmentStatus?: string | null;
  activeVehicleStatus?: string | null;
  activeShipmentStage?: number | null;
  activePickupName?: string | null;
  activeDeliveryName?: string | null;
  activeTransportModel?: string | null;
  sourceType?: 'own-fleet' | 'spot' | null;
};

type ActiveShipmentSummary = {
  id: number;
  shipmentNo: string;
  status: string;
  stage: number;
  pickupName: string;
  deliveryName: string;
  transportModel: string;
};

const PROCESS_STEPS = [
  'Atama Bekliyor', 'Atandı İşlem Bekliyor', 'Yükleme Bekliyor',
  'Yükleme Devam', 'Yükleme Tamam', 'Teslim Yolunda',
  'Boşaltma Bekliyor', 'Boşaltma Devam', 'Boşaltma Tamam',
];

const mapShipmentStageToProcessStep = (stage?: number | null) => {
  switch (Number(stage ?? -1)) {
    case 1: case 2: return 3;
    case 3: return 4;
    case 4: return 5;
    case 5: case 6: return 6;
    case 7: return 7;
    case 8: return 8;
    case 9: return 9;
    default: return 1;
  }
};

const isVehicleStatusActive = (value?: string | null) => {
  const normalized = String(value || '').trim().toLocaleUpperCase('tr-TR');
  return ['YÜK VERİLDİ', 'YOLDA', 'YÜKLEMEDE', 'YÜKLEME BEKLİYOR', 'TAHLİYE BEKLİYOR'].includes(normalized);
};

const isSpotVehiclePoint = (point: DashboardPoint) => point.sourceType === 'spot';

const formatDateTime = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('tr-TR');
};

const formatRelativeTime = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value).getTime();
  if (Number.isNaN(date)) return '-';
  const diff = Date.now() - date;
  if (diff < 60000) return 'Az önce';
  if (diff < 3600000) return `${Math.floor(diff / 60000)} dk önce`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)} sa önce`;
  return `${Math.floor(diff / 86400000)} gün önce`;
};

const formatPlate = (value?: string | null) => String(value || '').trim().toLocaleUpperCase('tr-TR');

const extractCityDistrictFromAddress = (address?: string | null) => {
  const raw = String(address || '').trim();
  if (!raw) return '-';
  const parts = raw.split(/[\/,-]/).map((item) => String(item || '').trim()).filter(Boolean);
  if (parts.length === 0) return '-';
  const tail = parts.slice(-3);
  const filtered = tail.filter((item) => {
    const normalized = item.toLocaleLowerCase('tr-TR');
    return !['turkiye', 'türkiye'].includes(normalized);
  });
  return filtered.join(' / ') || parts.slice(-2).join(' / ') || raw;
};

const getVehicleStatusColor = (point: DashboardPoint) => {
  const isMoving = (point.speed || 0) > 0 || point.isRunning;
  const hasActive = isVehicleStatusActive(point.activeVehicleStatus);
  const isSpot = isSpotVehiclePoint(point);
  if (isSpot) return { dot: '#2563eb', bg: 'bg-blue-500/20 text-blue-300 border-blue-500/30' };
  if (isMoving && hasActive) return { dot: '#16a34a', bg: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' };
  if (hasActive) return { dot: '#22c55e', bg: 'bg-green-500/20 text-green-300 border-green-500/30' };
  return { dot: '#7c3aed', bg: 'bg-violet-500/20 text-violet-300 border-violet-500/30' };
};

const parseActiveShipmentSummaries = (value?: string | null): ActiveShipmentSummary[] => {
  if (!value) return [];
  return String(value)
    .split('##')
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => {
      const [id, shipmentNo, status, stage, pickupName, deliveryName, transportModel] = item.split('||');
      return {
        id: Number(id || 0),
        shipmentNo: shipmentNo || '-',
        status: status || '-',
        stage: Number(stage || 0),
        pickupName: pickupName || '-',
        deliveryName: deliveryName || '-',
        transportModel: transportModel || '-',
      };
    })
    .filter((item) => Number.isFinite(item.id) && item.id > 0);
};

function LiveTrackingMap({
  points,
  onVehicleSelect,
  selectedVehicleId,
}: {
  points: DashboardPoint[];
  onVehicleSelect: (point: DashboardPoint) => void;
  selectedVehicleId?: string | null;
}) {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const layersRef = useRef<L.Layer[]>([]);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    const map = L.map(mapRef.current, {
      center: [39.0, 35.0],
      zoom: 6.5,
      zoomControl: true,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);

    mapInstanceRef.current = map;
    return () => { map.remove(); mapInstanceRef.current = null; };
  }, []);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    layersRef.current.forEach((layer) => layer.remove());
    layersRef.current = [];

    points.forEach((point) => {
      if (point.type !== 'vehicle') return;

      const statusColor = getVehicleStatusColor(point);
      const isMoving = (point.speed || 0) > 0 || point.isRunning;
      const radius = isMoving ? 9 : 7;

      if (isMoving) {
        const arrowIcon = L.divIcon({
          className: '',
          html: `<div style="width:0;height:0;border-left:11px solid transparent;border-right:11px solid transparent;border-bottom:20px solid #16a34a;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.25));transform:translate(-50%,-50%) rotate(0deg);"></div>`,
          iconSize: [22, 20],
          iconAnchor: [11, 10],
        });
        const arrowMarker = L.marker([point.latitude, point.longitude], { icon: arrowIcon, interactive: true }).addTo(map);
        arrowMarker.on('click', () => onVehicleSelect(point));
        layersRef.current.push(arrowMarker);

        const pulse = L.circleMarker([point.latitude, point.longitude], {
          radius: 16,
          fillColor: statusColor.dot,
          fillOpacity: 0.15,
          color: statusColor.dot,
          weight: 1.5,
          opacity: 0.4,
        }).addTo(map);
        layersRef.current.push(pulse);
      } else {
        const dot = L.circleMarker([point.latitude, point.longitude], {
          radius,
          fillColor: statusColor.dot,
          fillOpacity: 0.9,
          color: '#ffffff',
          weight: 2.5,
        }).addTo(map);
        dot.on('click', () => onVehicleSelect(point));
        layersRef.current.push(dot);
      }

      const labelIcon = L.divIcon({
        className: '',
        html: `<div style="position:absolute;left:0;top:0;transform:translate(-50%,-150%);background:${statusColor.dot};color:#fff;font-size:11px;font-weight:700;padding:3px 8px;border-radius:999px;white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,0.2);border:1px solid rgba(255,255,255,0.4);cursor:pointer;font-family:system-ui,-apple-system,sans-serif">${point.label}</div>`,
        iconSize: [0, 0],
        iconAnchor: [0, 0],
      });
      const labelMarker = L.marker([point.latitude, point.longitude], { icon: labelIcon, interactive: true }).addTo(map);
      labelMarker.on('click', () => onVehicleSelect(point));
      layersRef.current.push(labelMarker);
    });
  }, [points, onVehicleSelect]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !selectedVehicleId) return;
    const selectedPoint = points.find((point) => point.id === selectedVehicleId);
    if (!selectedPoint) return;
    map.flyTo([selectedPoint.latitude, selectedPoint.longitude], Math.max(map.getZoom(), 11), { duration: 1 });
  }, [selectedVehicleId, points]);

  return <div ref={mapRef} className="h-full w-full" />;
}

export function LiveTracking() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedVehiclePoint, setSelectedVehiclePoint] = useState<DashboardPoint | null>(null);
  const [selectedActiveShipment, setSelectedActiveShipment] = useState<{ point: DashboardPoint; shipment: ActiveShipmentSummary } | null>(null);
  const [sourceFilter, setSourceFilter] = useState<'own-fleet' | 'spot' | 'all'>('all');
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [recencyValue, setRecencyValue] = useState('120');
  const [recencyUnit, setRecencyUnit] = useState<'minutes' | 'hours' | 'days'>('minutes');
  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [response, systemSettings] = await Promise.all([
          dashboardService.getOverview(),
          settingsService.getSettingValue('settings_system_preferences', { spotLiveTrackingRecencyMinutes: '120' }),
        ]);
         setOverview(response.data);
         const configuredMinutes = Number((systemSettings as any)?.spotLiveTrackingRecencyMinutes || 120) || 0;
         setRecencyValue(String(configuredMinutes));
      } catch (error) {
        console.error('Canlı takip verileri yüklenemedi:', error);
        toast.error('Canlı takip verileri yüklenemedi');
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, []);

  useAutoRefresh(
    () => dashboardService.getOverview().then((response) => setOverview(response.data)).catch(() => {}),
    { intervalMs: 15000 }
  );

  const allPoints = useMemo(() => (overview?.map?.points || []) as DashboardPoint[], [overview]);

  const recencyMinutes = useMemo(() => {
    const value = Number(recencyValue);
    if (!Number.isFinite(value) || value <= 0) return 0;
    if (recencyUnit === 'hours') return value * 60;
    if (recencyUnit === 'days') return value * 24 * 60;
    return value;
  }, [recencyUnit, recencyValue]);

  const isPointWithinRecency = (point: DashboardPoint) => {
    if (!Number.isFinite(recencyMinutes) || recencyMinutes <= 0) return true;
    if (!point.locationUpdatedAt) return false;
    const timestamp = new Date(point.locationUpdatedAt).getTime();
    if (Number.isNaN(timestamp)) return false;
    return Date.now() - timestamp <= recencyMinutes * 60 * 1000;
  };

  const vehiclePoints = useMemo(() => {
    return allPoints.filter((point) => {
      if (point.type !== 'vehicle') return false;
       if (!isPointWithinRecency(point)) return false;
      return true;
    });
  }, [allPoints, recencyMinutes]);

  const exportFilteredVehicles = () => {
    const rows = filteredVehiclePoints.map((point) => ({
      Kaynak: isSpotVehiclePoint(point) ? 'Spot' : 'Özmal',
      Plaka: point.label,
      Dorse: point.trailerPlate || '',
      Sürücü: point.driverName || point.seyirDriverName || '',
      'Son Konum Zamanı': point.locationUpdatedAt ? formatDateTime(point.locationUpdatedAt) : '-',
      'Son Konumdan Geçen': point.locationUpdatedAt ? formatRelativeTime(point.locationUpdatedAt) : 'Konum yok',
      Enlem: point.latitude,
      Boylam: point.longitude,
      Adres: point.address || '',
      'Aktif Sevkiyat': point.activeShipmentNo || '',
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Canlı Takip');
    XLSX.writeFile(workbook, `canli-takip-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const filteredVehiclePoints = useMemo(() => {
    return vehiclePoints.filter((point) => {
      if (sourceFilter === 'own-fleet') return !isSpotVehiclePoint(point);
      if (sourceFilter === 'spot') return isSpotVehiclePoint(point);
      return true;
    }).filter((point) => {
      const q = vehicleSearch.trim().toLocaleLowerCase('tr-TR');
      if (!q) return true;
      return [point.label, point.trailerPlate, point.driverName, point.seyirDriverName, point.activeShipmentNo, point.activePickupName, point.activeDeliveryName]
        .join(' ').toLocaleLowerCase('tr-TR').includes(q);
    });
  }, [vehiclePoints, sourceFilter, vehicleSearch]);

  const mapPoints = useMemo(() => {
    return allPoints.filter((point) => {
      if (point.type !== 'vehicle') return true;
      return filteredVehiclePoints.some((vp) => vp.id === point.id);
    });
  }, [allPoints, filteredVehiclePoints]);

  const stats = useMemo(() => {
    const total = vehiclePoints.length;
    const ownFleet = vehiclePoints.filter((p) => !isSpotVehiclePoint(p)).length;
    const spot = vehiclePoints.filter((p) => isSpotVehiclePoint(p)).length;
    const moving = vehiclePoints.filter((p) => (p.speed || 0) > 0 || p.isRunning).length;
    const idle = vehiclePoints.filter((p) => !((p.speed || 0) > 0 || p.isRunning) && isVehicleStatusActive(p.activeVehicleStatus)).length;
    return { total, ownFleet, spot, moving, idle };
  }, [vehiclePoints]);

  const handleVehicleSelect = (point: DashboardPoint) => {
    setSelectedVehiclePoint(point);
    setShowDetail(true);
  };

  const handleBackToList = () => {
    setShowDetail(false);
    setTimeout(() => setSelectedVehiclePoint(null), 200);
  };

  const openDirectionsInGoogleMaps = (point: DashboardPoint) => {
    if (point.latitude == null || point.longitude == null) {
      toast.error('Araç konumu bulunamadı');
      return;
    }
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${point.latitude},${point.longitude}`)}&travelmode=driving`, '_blank', 'noopener,noreferrer');
  };

  const openActiveShipmentModal = (point: DashboardPoint, shipment: ActiveShipmentSummary) => {
    setSelectedActiveShipment({ point, shipment });
  };

  const navigateToShipmentDetail = () => {
    const shipmentNo = String(selectedActiveShipment?.shipment.shipmentNo || '').trim();
    if (!shipmentNo) return;
    setSelectedActiveShipment(null);
    navigate(`/tracking-list?q=${encodeURIComponent(shipmentNo)}`);
  };

  useEffect(() => {
    if (!selectedVehiclePoint) return;
    const updatedPoint = mapPoints.find((point) => point.id === selectedVehiclePoint.id);
    if (updatedPoint) setSelectedVehiclePoint(updatedPoint);
    else { setSelectedVehiclePoint(null); setShowDetail(false); }
  }, [mapPoints]);

  useEffect(() => {
    if (!selectedActiveShipment) return;
    const updatedPoint = mapPoints.find((point) => point.id === selectedActiveShipment.point.id);
    if (!updatedPoint) {
      setSelectedActiveShipment(null);
      return;
    }
    const updatedShipment = parseActiveShipmentSummaries(updatedPoint.activeShipmentSummaries).find((item) => item.id === selectedActiveShipment.shipment.id);
    if (!updatedShipment) {
      setSelectedActiveShipment(null);
      return;
    }
    setSelectedActiveShipment({ point: updatedPoint, shipment: updatedShipment });
  }, [mapPoints]);

  return (
    <>
    <div className="flex h-[calc(100vh-64px)] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800">
      {/* MAP */}
      <div className="relative flex-1 min-w-0 bg-slate-100">
        {loading ? (
          <div className="flex h-full items-center justify-center text-slate-400">Yükleniyor...</div>
        ) : mapPoints.length > 0 ? (
          <LiveTrackingMap points={mapPoints} onVehicleSelect={handleVehicleSelect} selectedVehicleId={selectedVehiclePoint?.id || null} />
        ) : (
          <div className="flex h-full items-center justify-center text-slate-400">Harita verisi bulunmuyor</div>
        )}

        {/* LEGEND */}
        <div className="absolute bottom-4 left-4 flex flex-wrap gap-3 rounded-2xl bg-white/95 px-4 py-3 text-[11px] text-slate-600 shadow-lg backdrop-blur dark:bg-black/80 dark:text-slate-300">
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />Hareket Halinde: {stats.moving}</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-green-500" />Aktif Görevli: {stats.idle}</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-violet-500" />Boşta: {stats.total - stats.moving - stats.idle - stats.spot}</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-blue-500" />Spot: {stats.spot}</span>
        </div>
      </div>

      {/* RIGHT PANEL */}
      <aside className="flex w-[420px] flex-col border-l border-slate-200 bg-white dark:border-slate-700 dark:bg-[#0d0d0d]">
        {/* HEADER */}
        <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-slate-800">
          <div>
            <div className="flex items-center gap-2 text-lg font-bold text-slate-800 dark:text-slate-100">
              <Truck className="h-5 w-5 text-orange-500" />
              Filo Yönetimi
            </div>
            <div className="mt-0.5 text-[11px] text-slate-400 dark:text-slate-500">
              {stats.total} araç · {stats.moving} hareket halinde
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 rounded-lg bg-emerald-500/10 px-2.5 py-1 text-[12px] font-semibold text-emerald-600 dark:text-emerald-400">
              <Activity className="h-3.5 w-3.5" />
              {stats.moving}
            </div>
            <div className="flex items-center gap-1 rounded-lg bg-slate-100 px-2.5 py-1 text-[12px] font-semibold text-slate-500 dark:bg-slate-800 dark:text-slate-400">
              <span>{stats.ownFleet}</span>
              <span className="text-slate-300 dark:text-slate-600">/</span>
              <span>{stats.spot}</span>
            </div>
          </div>
        </div>

        {/* SEARCH + TABS */}
        <div className="shrink-0 space-y-3 border-b border-slate-200 p-4 dark:border-slate-800">
           <label className="relative block">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={vehicleSearch}
              onChange={(e) => setVehicleSearch(e.target.value)}
              placeholder="Plaka, dorse, sürücü veya sevkiyat ara..."
              className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 pl-10 pr-4 text-[13px] text-slate-800 outline-none placeholder:text-slate-400 focus:border-orange-400 focus:ring-1 focus:ring-orange-400 dark:border-slate-700 dark:bg-[#151515] dark:text-slate-100 dark:placeholder:text-slate-500"
            />
           </label>
           <div className="flex items-center gap-2">
             <label className="flex flex-1 items-center gap-2 text-[11px] font-semibold text-slate-500 dark:text-slate-400">
               Son konum yaşı
               <input type="number" min="0" value={recencyValue} onChange={(event) => setRecencyValue(event.target.value)} className="h-9 w-20 rounded-lg border border-slate-200 bg-slate-50 px-2 text-xs text-slate-800 outline-none focus:border-orange-400 dark:border-slate-700 dark:bg-[#151515] dark:text-slate-100" />
               <select value={recencyUnit} onChange={(event) => setRecencyUnit(event.target.value as 'minutes' | 'hours' | 'days')} className="h-9 rounded-lg border border-slate-200 bg-slate-50 px-2 text-xs text-slate-800 outline-none dark:border-slate-700 dark:bg-[#151515] dark:text-slate-100">
                 <option value="minutes">dakika</option>
                 <option value="hours">saat</option>
                 <option value="days">gün</option>
               </select>
             </label>
             <button type="button" onClick={exportFilteredVehicles} className="inline-flex h-9 items-center gap-1 rounded-lg border border-emerald-300 bg-emerald-50 px-2.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200">
               <Download className="h-3.5 w-3.5" /> Excel
             </button>
           </div>
          <div className="flex gap-1.5">
            {[
              { value: 'all', label: 'Tümü', count: stats.total },
              { value: 'own-fleet', label: 'Özmal', count: stats.ownFleet },
              { value: 'spot', label: 'Spot', count: stats.spot },
            ].map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => {
                  setSourceFilter(tab.value as 'own-fleet' | 'spot' | 'all');
                  setVehicleSearch('');
                  setShowDetail(false);
                  setSelectedVehiclePoint(null);
                }}
                className={`flex-1 rounded-xl px-3 py-2 text-[12px] font-semibold transition-all ${sourceFilter === tab.value ? 'bg-orange-500 text-white shadow-sm' : 'bg-slate-100 text-slate-500 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700'}`}
              >
                {tab.label}
                <span className={`ml-1.5 inline-flex h-4 min-w-[18px] items-center justify-center rounded-full px-1.5 text-[10px] font-bold ${sourceFilter === tab.value ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-400'}`}>{tab.count}</span>
              </button>
            ))}
          </div>
        </div>

        {/* LIST / DETAIL */}
        <div className="flex-1 overflow-y-auto">
          {showDetail && selectedVehiclePoint ? (
            /* DETAIL VIEW */
            <div className="p-4">
              <button type="button" onClick={handleBackToList} className="mb-3 inline-flex items-center gap-1.5 text-[12px] font-medium text-orange-500 hover:text-orange-400">
                <ChevronLeft className="h-4 w-4" />Listeye Dön
              </button>

              <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-5 shadow-sm dark:border-slate-700 dark:from-[#141414] dark:to-[#0d0d0d]">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xl font-bold text-slate-800 dark:text-slate-100">{selectedVehiclePoint.label}</div>
                    <div className="mt-1 text-[13px] text-slate-400">{selectedVehiclePoint.trailerPlate || '-'}</div>
                    <div className="mt-1 text-[12px] font-medium text-slate-500 dark:text-slate-400">{extractCityDistrictFromAddress(selectedVehiclePoint.address)}</div>
                    <div className="mt-1 flex items-center gap-1.5 text-sm font-medium text-slate-600 dark:text-slate-300">
                       <User className="h-3.5 w-3.5 text-slate-400" />
                       {selectedVehiclePoint.driverName || selectedVehiclePoint.seyirDriverName || '-'}{isSpotVehiclePoint(selectedVehiclePoint) && selectedVehiclePoint.driverPhone ? ` • ${selectedVehiclePoint.driverPhone}` : ''}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12px] font-bold ${(selectedVehiclePoint.speed || 0) > 0 ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' : 'bg-amber-500/15 text-amber-600 dark:text-amber-400'}`}>
                      <Gauge className="h-3.5 w-3.5" />
                      {selectedVehiclePoint.speed != null ? `${selectedVehiclePoint.speed} km/h` : '-'}
                    </div>
                    <div className="mt-1.5 text-[11px] text-slate-400">
                      {selectedVehiclePoint.isRunning ? 'Motor Çalışıyor' : 'Motor Kapalı'}
                    </div>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3 text-[12px]">
                  <div className="rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-[#111]">
                    <div className="text-slate-400 dark:text-slate-500">Günlük KM</div>
                    <div className="mt-1 font-semibold text-slate-800 dark:text-slate-200">{selectedVehiclePoint.dailyKm != null ? `${selectedVehiclePoint.dailyKm.toLocaleString('tr-TR')} km` : '-'}</div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-[#111]">
                    <div className="text-slate-400 dark:text-slate-500">Yakıt Seviyesi</div>
                    <div className="mt-1 font-semibold text-slate-800 dark:text-slate-200">{selectedVehiclePoint.fuelLevel != null ? `%${selectedVehiclePoint.fuelLevel.toLocaleString('tr-TR', { maximumFractionDigits: 1 })}` : '-'}</div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-[#111]">
                    <div className="text-slate-400 dark:text-slate-500">Günlük Yakıt</div>
                    <div className="mt-1 font-semibold text-slate-800 dark:text-slate-200">{selectedVehiclePoint.dailyFuel != null ? `${selectedVehiclePoint.dailyFuel.toLocaleString('tr-TR')} lt` : '-'}</div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-[#111]">
                    <div className="text-slate-400 dark:text-slate-500">Konum</div>
                    <div className="mt-1 font-semibold text-slate-800 dark:text-slate-200">{formatRelativeTime(selectedVehiclePoint.locationUpdatedAt)}</div>
                  </div>
                </div>

                <div className="mt-4 space-y-2.5 text-[12px]">
                  {[
                    ['Araç Durumu', selectedVehiclePoint.activeVehicleStatus || '-'],
                    ['Kaynak', isSpotVehiclePoint(selectedVehiclePoint) ? 'Spot' : 'Özmal'],
                    ['Sürücü', `${selectedVehiclePoint.driverName || '-'}${isSpotVehiclePoint(selectedVehiclePoint) && selectedVehiclePoint.driverPhone ? ` • ${selectedVehiclePoint.driverPhone}` : ''}`],
                    ['Seyir Sürücü', selectedVehiclePoint.seyirDriverName || '-'],
                    ['Treyler', formatPlate(selectedVehiclePoint.trailerPlate) || '-'],
                    ['Günlük Sürüş', selectedVehiclePoint.dailyDriveTime || '-'],
                    ['Günlük Çalışma', selectedVehiclePoint.dailyWorkTime || '-'],
                    ['Rölanti', selectedVehiclePoint.dailyIdleTime || '-'],
                    ['Haftalık', selectedVehiclePoint.currentWeekHours || '-'],
                    ['2 Haftalık', selectedVehiclePoint.twoWeekHours || '-'],
                    ['Kontak Açık', selectedVehiclePoint.ignitionOn || '-'],
                    ['Kontak Kapalı', selectedVehiclePoint.ignitionOff || '-'],
                  ].map(([label, value]) => (
                    <div key={label} className="flex items-center justify-between border-b border-slate-100 pb-2 dark:border-slate-800">
                      <span className="text-slate-400 dark:text-slate-500">{label}</span>
                      <span className="font-medium text-slate-700 dark:text-slate-300">{value}</span>
                    </div>
                  ))}
                </div>

                {parseActiveShipmentSummaries(selectedVehiclePoint.activeShipmentSummaries).length > 0 && (
                  <div className="mt-4 block w-full rounded-xl border border-orange-200 bg-orange-50/50 p-3 text-left dark:border-orange-800/70 dark:bg-orange-950/20">
                    <div className="flex items-center gap-2 text-[12px] font-semibold text-slate-600 dark:text-slate-300">
                      <MapPin className="h-3.5 w-3.5 text-orange-500" />
                      Aktif Sevkiyat
                      <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-bold text-orange-700 dark:bg-orange-900/40 dark:text-orange-300">Siparişe Tıklayın</span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {parseActiveShipmentSummaries(selectedVehiclePoint.activeShipmentSummaries).map((shipment) => (
                        <button
                          key={shipment.id}
                          type="button"
                          onClick={() => openActiveShipmentModal(selectedVehiclePoint, shipment)}
                          className="inline-flex items-center gap-2 rounded-lg border border-orange-300 bg-white px-3 py-2 text-[12px] font-semibold text-orange-700 shadow-sm hover:bg-orange-100 dark:border-orange-700 dark:bg-[#171717] dark:text-orange-300 dark:hover:bg-orange-950/40"
                        >
                          <span className="underline underline-offset-2">{shipment.shipmentNo}</span>
                          <span className="text-[11px] text-slate-500 dark:text-slate-400">{shipment.pickupName} → {shipment.deliveryName}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-5 flex flex-wrap gap-2">
                  <button type="button" onClick={() => openDirectionsInGoogleMaps(selectedVehiclePoint)} className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2 text-[12px] font-medium text-slate-700 shadow-sm hover:bg-slate-50 dark:border-slate-700 dark:bg-[#151515] dark:text-slate-200 dark:hover:bg-slate-800">
                    <Navigation className="h-4 w-4" />Google Maps'te Aç
                  </button>
                  {selectedVehiclePoint.activeShipmentId && (
                    <button type="button" onClick={() => openActiveShipmentModal(selectedVehiclePoint)} className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2 text-[12px] font-medium text-slate-700 shadow-sm hover:bg-slate-50 dark:border-slate-700 dark:bg-[#151515] dark:text-slate-200 dark:hover:bg-slate-800">
                      Sipariş Detayı <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            /* VEHICLE LIST */
            <div key={`vehicle-list-${sourceFilter}`} className="divide-y divide-slate-100 dark:divide-slate-800">
              {filteredVehiclePoints.length === 0 ? (
                <div className="flex flex-col items-center justify-center px-6 py-20 text-center">
                  <Truck className="mb-3 h-10 w-10 text-slate-300 dark:text-slate-600" />
                  <div className="text-sm font-medium text-slate-400 dark:text-slate-500">Araç bulunamadı</div>
                  <div className="mt-1 text-[12px] text-slate-400 dark:text-slate-500">Filtreleri değiştirmeyi deneyin</div>
                </div>
              ) : filteredVehiclePoints.map((point) => {
                const statusColor = getVehicleStatusColor(point);
                const isMoving = (point.speed || 0) > 0 || point.isRunning;
                const isSelected = selectedVehiclePoint?.id === point.id;
                return (
                  <button
                    key={point.id}
                    type="button"
                    onClick={() => handleVehicleSelect(point)}
                    className={`flex w-full items-start gap-3 px-5 py-3.5 text-left transition-all hover:bg-slate-50 dark:hover:bg-slate-900/50 ${isSelected ? 'bg-orange-50/80 ring-1 ring-orange-200 dark:bg-orange-950/20 dark:ring-orange-800/50' : ''}`}
                  >
                    <div className={`mt-0.5 h-2.5 w-2.5 shrink-0 rounded-full ${isMoving ? 'animate-pulse' : ''}`} style={{ backgroundColor: statusColor.dot }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-bold text-slate-800 dark:text-slate-100 truncate">{formatPlate(point.label) || point.label}</div>
                        <div className={`flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold ${(point.speed || 0) > 0 ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500'}`}>
                          <Gauge className="h-3 w-3" />
                          {point.speed != null ? `${point.speed}` : '-'}
                        </div>
                      </div>
                      <div className="mt-0.5 text-[12px] text-slate-400 dark:text-slate-500 truncate">{formatPlate(point.trailerPlate) || '-'}</div>
                      <div className="mt-0.5 text-[11px] font-medium text-slate-500 dark:text-slate-400 truncate">{extractCityDistrictFromAddress(point.address)}</div>
                      <div className="mt-0.5 flex items-center gap-1.5 text-[12px] font-medium text-slate-600 dark:text-slate-300">
                        <User className="h-3 w-3 shrink-0 text-slate-400" />
                        <span className="truncate">{point.driverName || point.seyirDriverName || '-'}{isSpotVehiclePoint(point) && point.driverPhone ? ` • ${point.driverPhone}` : ''}</span>
                      </div>
                      {parseActiveShipmentSummaries(point.activeShipmentSummaries).length > 0 ? (
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          {parseActiveShipmentSummaries(point.activeShipmentSummaries).map((shipment) => (
                            <span
                              key={shipment.id}
                              onClick={(event) => {
                                event.stopPropagation();
                                openActiveShipmentModal(point, shipment);
                              }}
                              className="inline-flex cursor-pointer items-center rounded-lg border border-orange-200 bg-orange-50 px-2 py-1 text-[11px] leading-4 text-orange-700 hover:bg-orange-100 dark:border-orange-800 dark:bg-orange-950/30 dark:text-orange-300 dark:hover:bg-orange-950/50"
                            >
                              {shipment.shipmentNo}
                            </span>
                          ))}
                        </div>
                      ) : null}
                      <div className="mt-1 flex items-center gap-3 text-[10px] text-slate-400 dark:text-slate-500">
                        <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{formatRelativeTime(point.locationUpdatedAt)}</span>
                        {point.activeVehicleStatus && <span className="truncate">{point.activeVehicleStatus}</span>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </aside>
    </div>
    {selectedActiveShipment?.shipment.id && typeof document !== 'undefined'
      ? createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4" onClick={() => setSelectedActiveShipment(null)}>
            <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-[#111111]" onClick={(event) => event.stopPropagation()}>
              <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-800">
                <div className="text-lg font-bold text-slate-900 dark:text-slate-100">Sipariş Detayı</div>
                <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">Aktif sevkiyat bilgileri</div>
              </div>
              <div className="space-y-3 px-5 py-4 text-sm">
                {[
                  ['Sipariş No', selectedActiveShipment.shipment.shipmentNo || '-'],
                  ['Durum', selectedActiveShipment.shipment.status || '-'],
                  ['Aşama', selectedActiveShipment.shipment.stage ? PROCESS_STEPS[Math.max(0, Math.min(PROCESS_STEPS.length - 1, mapShipmentStageToProcessStep(selectedActiveShipment.shipment.stage) - 1))] : '-'],
                  ['Kaynak', isSpotVehiclePoint(selectedActiveShipment.point) ? 'Spot' : 'Özmal'],
                  ['Yükleme', selectedActiveShipment.shipment.pickupName || '-'],
                  ['Boşaltma', selectedActiveShipment.shipment.deliveryName || '-'],
                  ['Sürücü', `${selectedActiveShipment.point.driverName || selectedActiveShipment.point.seyirDriverName || '-'}${isSpotVehiclePoint(selectedActiveShipment.point) && selectedActiveShipment.point.driverPhone ? ` • ${selectedActiveShipment.point.driverPhone}` : ''}`],
                  ['Çekici', formatPlate(selectedActiveShipment.point.label) || '-'],
                  ['Dorse', formatPlate(selectedActiveShipment.point.trailerPlate) || '-'],
                ].map(([label, value]) => (
                  <div key={String(label)} className="flex items-start justify-between gap-4 border-b border-slate-100 pb-2 dark:border-slate-800">
                    <span className="text-slate-400 dark:text-slate-500">{label}</span>
                    <span className="text-right font-medium text-slate-700 dark:text-slate-300">{value}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-end gap-3 border-t border-slate-200 px-5 py-4 dark:border-slate-800">
                <button type="button" onClick={() => setSelectedActiveShipment(null)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-900">
                  Kapat
                </button>
                <button type="button" onClick={navigateToShipmentDetail} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-black hover:bg-blue-700 dark:text-white">
                  Sipariş Detayına Git
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )
      : null}
    </>
  );
}
